import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { FiCheck, FiX, FiAward } from 'react-icons/fi';
import confetti from 'canvas-confetti';

const QuizResult = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [results, setResults] = useState(null);

  useEffect(() => {
    // Try to get results from location state first (more reliable)
    const stateResults = location.state?.results;
    
    // If not in state, try sessionStorage
    const storedResults = sessionStorage.getItem('quizResults');
    
    if (stateResults) {
      // Use results from navigation state
      const resultsWithNumericPercentage = {
        ...stateResults,
        percentage: typeof stateResults.percentage === 'string' 
          ? parseFloat(stateResults.percentage) 
          : stateResults.percentage
      };
      
      setResults(resultsWithNumericPercentage);
      
      // Celebrate if scored above 70%
      if (resultsWithNumericPercentage.percentage >= 70) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      }
    } else if (storedResults) {
      try {
        const parsedResults = JSON.parse(storedResults);
        
        // Ensure percentage is a number
        const resultsWithNumericPercentage = {
          ...parsedResults,
          percentage: typeof parsedResults.percentage === 'string' 
            ? parseFloat(parsedResults.percentage) 
            : parsedResults.percentage
        };
        
        setResults(resultsWithNumericPercentage);
        sessionStorage.removeItem('quizResults');

        // Celebrate if scored above 70%
        if (resultsWithNumericPercentage.percentage >= 70) {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        }
      } catch (error) {
        console.error('Error parsing quiz results:', error);
        navigate('/quizzes');
      }
    } else {
      // No results found, redirect to quizzes
      console.log('No quiz results found');
      navigate('/quizzes');
    }
  }, [navigate, location.state]);

  if (!results) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600">Loading results...</p>
        </div>
      </div>
    );
  }

  const getScoreColor = (percentage) => {
    const numPercentage = Number(percentage);
    if (numPercentage >= 80) return 'text-green-600';
    if (numPercentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreMessage = (percentage) => {
    const numPercentage = Number(percentage);
    if (numPercentage >= 90) return 'Outstanding! 🎉';
    if (numPercentage >= 80) return 'Excellent Work! 👏';
    if (numPercentage >= 70) return 'Great Job! 👍';
    if (numPercentage >= 60) return 'Good Effort! 💪';
    return 'Keep Practicing! 📚';
  };

  const percentageDisplay = Number(results.percentage).toFixed(1);

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn">
      <div className="card text-center mb-6">
        <FiAward className="mx-auto text-primary-600 mb-4" size={64} />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Quiz Completed!</h1>
        <p className="text-xl text-gray-600 mb-4">{getScoreMessage(results.percentage)}</p>
        
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <p className="text-5xl font-bold mb-2">
            <span className={getScoreColor(results.percentage)}>
              {results.score}
            </span>
            <span className="text-gray-400 text-3xl">/{results.totalPoints}</span>
          </p>
          <p className="text-lg text-gray-600">
            {percentageDisplay}% Correct
          </p>
        </div>

        <div className="flex justify-center space-x-4">
          <Link to="/classrooms" className="btn-secondary">
            Back to Classrooms
          </Link>
          <Link to={`/leaderboard/quiz/${id}`} className="btn-primary">
            View Leaderboard
          </Link>
        </div>
      </div>

      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Review Your Answers
        </h2>

        <div className="space-y-6">
          {results.questions?.map((question, index) => (
            <div
              key={question._id || index}
              className={`p-4 rounded-lg border-2 ${
                question.isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-medium text-gray-900">
                  {index + 1}. {question.question}
                </h3>
                <div className="flex items-center">
                  {question.isCorrect ? (
                    <FiCheck className="text-green-600 mr-1" />
                  ) : (
                    <FiX className="text-red-600 mr-1" />
                  )}
                  <span className="text-sm font-medium">
                    {question.isCorrect ? question.points : 0}/{question.points} pts
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                {question.options?.map((option, optionIndex) => (
                  <div
                    key={optionIndex}
                    className={`p-2 rounded flex items-start ${
                      optionIndex === question.correctAnswer
                        ? 'bg-green-100 font-medium text-green-800'
                        : optionIndex === question.userAnswer && !question.isCorrect
                        ? 'bg-red-100 text-red-800'
                        : ''
                    }`}
                  >
                    <span className="mr-2 flex-shrink-0">
                      {optionIndex === question.correctAnswer && '✓'}
                      {optionIndex === question.userAnswer && !question.isCorrect && '✗'}
                      {optionIndex === question.userAnswer && question.isCorrect && '✓'}
                    </span>
                    <span>{option}</span>
                  </div>
                ))}
              </div>

              {!question.isCorrect && question.userAnswer !== null && question.userAnswer !== undefined && (
                <div className="mt-3 p-2 bg-gray-100 rounded text-sm">
                  <p className="text-gray-700">
                    <span className="font-medium">Your answer:</span> Option {(question.userAnswer || 0) + 1}
                  </p>
                  <p className="text-gray-700">
                    <span className="font-medium">Correct answer:</span> Option {question.correctAnswer + 1}
                  </p>
                </div>
              )}
              
              {question.userAnswer === null || question.userAnswer === undefined && (
                <div className="mt-3 p-2 bg-yellow-100 rounded text-sm">
                  <p className="text-yellow-800">
                    Not answered • Correct answer: Option {question.correctAnswer + 1}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Want to improve your score? Practice more quizzes in your classroom!
        </p>
      </div>
    </div>
  );
};

export default QuizResult;